'use client';

import React, { Suspense, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Sidebar from '@/components/common/Sidebar';
import Header from '@/components/common/Header';
import SyncSuccessToast from '@/components/common/SyncSuccessToast';
import ErrorBoundary from '@/components/common/ErrorBoundary';

/** Routes that are allowed without an active paid subscription (choose-a-plan flows). */
const ALLOWED_WITHOUT_SUBSCRIPTION = ['/public-subscription-selection', '/pricing', '/verify-subscription', '/luxe-mock'];

function DashboardLayoutLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#eeeced' }}>
      <div className="rounded-full h-10 w-10 border-2 border-[#236434] border-t-transparent animate-spin" />
    </div>
  );
}

function DashboardLayoutInner({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasPaidAccess, setHasPaidAccess] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      router.replace('/login-authentication');
    }
  }, [user, loading, router]);

  // Gate: no dashboard access without active subscription (or founder legacy), except plan-selection routes or mock Luxe
  useEffect(() => {
    if (!user || loading) return;

    const pathAllowed = ALLOWED_WITHOUT_SUBSCRIPTION.some((p) => pathname === p || pathname?.startsWith(p + '/'));
    const mockLuxe = searchParams.get('mock_plan') === 'Luxe';
    if (pathAllowed || mockLuxe) {
      setSubscriptionChecked(true);
      setHasPaidAccess(true);
      return;
    }

    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('subscription_status, founder_legacy_until')
      .eq('id', user.id)
      .single()
      .then(({ data, error }) => {
        if (error) {
          setSubscriptionChecked(true);
          setHasPaidAccess(false);
          return;
        }
        const active = data?.subscription_status === 'active';
        const legacyValid =
          data?.founder_legacy_until &&
          new Date(data.founder_legacy_until) > new Date();
        setHasPaidAccess(!!(active || legacyValid));
        setSubscriptionChecked(true);
      });
  }, [user, loading, pathname, searchParams]);

  // Redirect to plan selection if user has no paid access and is not on an allowed route
  useEffect(() => {
    if (!subscriptionChecked || hasPaidAccess) return;
    const pathAllowed = ALLOWED_WITHOUT_SUBSCRIPTION.some((p) => pathname === p || pathname?.startsWith(p + '/'));
    if (!pathAllowed) {
      router.replace('/public-subscription-selection');
    }
  }, [subscriptionChecked, hasPaidAccess, pathname, router]);

  // Close mobile menu when route changes (e.g. after clicking a nav link)
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const isClientEnrollment = pathname?.includes('client-enrollment');

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#eeeced' }}>
        <div className="rounded-full h-10 w-10 border-2 border-[#236434] border-t-transparent animate-spin" />
      </div>
    );
  }

  // While checking subscription, show loader (unless on allowed route)
  const pathAllowed = ALLOWED_WITHOUT_SUBSCRIPTION.some((p) => pathname === p || pathname?.startsWith(p + '/'));
  if (!pathAllowed && (!subscriptionChecked || !hasPaidAccess)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#eeeced' }}>
        <div className="rounded-full h-10 w-10 border-2 border-[#236434] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-row min-h-screen w-full overflow-x-hidden"
      style={{ backgroundColor: isClientEnrollment ? '#ffffff' : '#eeeced' }}
    >
      {/* Desktop: sidebar always visible in flow */}
      <div className="hidden lg:block flex-shrink-0 pt-4 pb-4 pl-4 sticky top-0 h-[calc(100vh-2rem)] self-start">
        <Sidebar />
      </div>
      {/* Mobile: overlay when menu open */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="relative z-10 w-[280px] max-w-[85vw] h-full bg-white shadow-xl overflow-y-auto">
            <Sidebar onClose={() => setMobileMenuOpen(false)} isMobileOverlay />
          </div>
        </div>
      )}
      <main
        className="flex-1 min-w-0 min-h-screen overflow-x-hidden flex flex-col pb-0 w-full"
        style={{
          backgroundColor: isClientEnrollment ? '#ffffff' : '#eeeced',
          boxSizing: 'border-box',
        }}
      >
        <Header onMenuClick={() => setMobileMenuOpen(true)} />
        <div
          className={`flex-1 w-full pt-0 pb-8 min-h-0 overflow-y-auto overflow-x-hidden -mt-[10px] ${
            isClientEnrollment
              ? 'bg-white pl-4 md:pl-6 lg:pl-[48px] pr-0'
              : 'px-4 md:px-6 lg:pl-[48px] lg:pr-[48px]'
          }`}
        >
          <ErrorBoundary key={pathname ?? 'default'}>
            {children}
          </ErrorBoundary>
        </div>
      </main>
      <SyncSuccessToast />
    </div>
  );
}

export default function DashboardLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return (
    <Suspense fallback={<DashboardLayoutLoader />}>
      <DashboardLayoutInner>{children}</DashboardLayoutInner>
    </Suspense>
  );
}